import React, { useState } from "react";
import { InfoRow, SectionTitle } from "./UI";
import { EDITABLE_FIELDS, getClientOverrides, saveClientOverrides } from "../utils/clientOverrides";

// מחשב גיל בפורמט שנים.חודשים מתאריך DD/MM/YYYY → למשל 66.11
function calcAge(bday) {
  if (!bday) return null;
  const p = bday.split('/');
  if (p.length !== 3) return null;
  const birth = new Date(+p[2], +p[1] - 1, +p[0]);
  if (isNaN(birth)) return null;
  const now = new Date();
  let years  = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth()    - birth.getMonth();
  if (now.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  return `${years}.${months}`;
}

const MARITAL_OPTIONS = ["רווק/ה", "נשוי/אה", "גרוש/ה", "אלמן/ה", "ידוע/ה בציבור"];

const S = {
  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: "20px",
  },
  panel: {
    background: "white",
    width: "100%",
    maxWidth: "800px",
    borderRadius: "16px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
  },
  header: {
    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
    padding: "16px 20px",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  body: {
    padding: "24px",
    overflowY: "auto",
    direction: "rtl",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "24px",
  },
  closeBtn: {
    background: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "white",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    transition: "all 0.2s",
  },
  headerBtn: {
    background: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "white",
    padding: "6px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 700,
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "5px 0",
  },
  fieldLabel: {
    fontSize: 12,
    color: "#64748b",
    width: 100,
    flexShrink: 0,
    textAlign: "right",
  },
  input: {
    flex: 1,
    padding: "7px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 7,
    fontSize: 13,
    outline: "none",
    direction: "rtl",
    textAlign: "right",
    background: "white",
  },
};

function SubSection({ title, color, children }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <SectionTitle color={color}>{title}</SectionTitle>
      {children}
    </div>
  );
}

export default function ClientDetailsModal({ client, employer, agent, onClose, inline, onSaved }) {
  // client = הנתונים כפי שהגיעו מהקבצים (ללא דריסות); הדריסות ממוזגות כאן
  const clientKey = client?.id;
  const [overrides, setOverrides] = useState(() => getClientOverrides(clientKey));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  if (!client) return null;

  const merged = { ...client, ...overrides };

  const startEdit = () => {
    const d = {};
    for (const { key } of EDITABLE_FIELDS) d[key] = merged[key] || "";
    setDraft(d);
    setEditing(true);
  };

  const handleSave = () => {
    // נשמרים רק שדות ששונים מנתוני המקור — כך קובץ חדש ימשיך לעדכן שדות שלא נערכו
    const toSave = {};
    for (const { key } of EDITABLE_FIELDS) {
      const v = (draft[key] || "").trim();
      if (v && v !== String(client[key] || "")) toSave[key] = v;
    }
    saveClientOverrides(clientKey, toSave);
    setOverrides(getClientOverrides(clientKey));
    setEditing(false);
    if (onSaved) onSaved();
  };

  const modalStyle = {
    ...S.modal,
    position: inline ? "absolute" : "fixed",
    background: inline ? "rgba(30, 41, 59, 0.4)" : S.modal.background,
    backdropFilter: inline ? "blur(2px)" : S.modal.backdropFilter,
    zIndex: inline ? 100 : S.modal.zIndex,
    padding: inline ? "12px" : S.modal.padding,
  };

  const panelStyle = {
    ...S.panel,
    borderRadius: 16, // Always rounded
    maxWidth: inline ? "100%" : S.panel.maxWidth,
    maxHeight: inline ? "95%" : S.panel.maxHeight,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
  };

  const maritalOpts = MARITAL_OPTIONS.includes(draft.marital) || !draft.marital
    ? MARITAL_OPTIONS
    : [draft.marital, ...MARITAL_OPTIONS];

  return (
    <div style={modalStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        <div style={S.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "24px" }}>👤</span>
            <div style={{ fontWeight: 800, fontSize: "18px" }}>פרטי לקוח - {client.name}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {clientKey && !editing && (
              <button onClick={startEdit} style={S.headerBtn} title="עריכת פרטי קשר">✏️ ערוך</button>
            )}
            <button onClick={onClose} style={S.closeBtn} title="סגור">✕</button>
          </div>
        </div>

        <div style={S.body}>
          <div style={S.grid}>
            <SubSection title="פרטים אישיים" color="#2563eb">
              <InfoRow label="שם מלא" value={client.name} bold />
              <InfoRow label="תעודת זהות" value={client.id} />
              <InfoRow label="תאריך לידה" value={client.bday ? `${client.bday}${calcAge(client.bday) ? `  (גיל ${calcAge(client.bday)})` : ''}` : null} />
              <InfoRow label="מין" value={client.gender} />
              <InfoRow label="מספר ילדים" value={client.children} />
              {!editing && (
                <>
                  <InfoRow label="מצב משפחתי" value={merged.marital} />
                  <InfoRow label="עיר" value={merged.city} />
                  <InfoRow label="כתובת" value={merged.street && `${merged.street} ${merged.houseNum || ""}`} />
                  <InfoRow label="מיקוד" value={merged.zip} />
                  <InfoRow label="טלפון" value={merged.phone} />
                  <InfoRow label="נייד" value={merged.mobile} />
                  <InfoRow label="דוא״ל" value={merged.email} />
                </>
              )}
            </SubSection>

            {editing && (
              <SubSection title="עריכת פרטי קשר" color="#d97706">
                {EDITABLE_FIELDS.map(({ key, label }) => (
                  <div key={key} style={S.fieldRow}>
                    <span style={S.fieldLabel}>{label}</span>
                    {key === "marital" ? (
                      <select
                        value={draft.marital || ""}
                        onChange={e => setDraft(d => ({ ...d, marital: e.target.value }))}
                        style={S.input}
                      >
                        <option value="">—</option>
                        {maritalOpts.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={key === "email" ? "email" : "text"}
                        value={draft[key] || ""}
                        onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                        style={{
                          ...S.input,
                          ...(key === "email" ? { direction: "ltr", textAlign: "left" } : null),
                        }}
                      />
                    )}
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-start" }}>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: "8px 22px", background: "#059669", color: "white",
                      border: "none", borderRadius: 8, cursor: "pointer",
                      fontWeight: 700, fontSize: 13,
                    }}
                  >💾 שמור</button>
                  <button
                    onClick={() => setEditing(false)}
                    style={{
                      padding: "8px 18px", background: "white", color: "#64748b",
                      border: "1px solid #cbd5e1", borderRadius: 8, cursor: "pointer",
                      fontWeight: 700, fontSize: 13,
                    }}
                  >ביטול</button>
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                  השינויים נשמרים מעל נתוני המסלקה — טעינת קובץ חדש לא תדרוס אותם. ניקוי שדה מחזיר את הערך מהקובץ.
                </div>
              </SubSection>
            )}

            {!editing && <div></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
